import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Body } from '@swyftx/aviary/atoms/Typography';
import { types } from '@swyftx/marketing-utils';

import { UserStore } from '@shared/store';

import { RewardsContext } from '@routes/Rewards/Rewards.context';
import { RewardType } from '@routes/Rewards/types';

import { DateTime, Duration } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useInterval } from 'react-use';

type Props = {
  reward: types.UserAssetReward;
};

const RewardCardCountdown: React.FC<Props> = observer(({ reward }) => {
  const [diff, setDiff] = useState<Duration | undefined>(undefined);
  const [dateToUse, setDateToUse] = useState<DateTime>();
  const { t } = useTranslation('rewards', { keyPrefix: 'countdown' });
  const { fetchRewards } = useContext(RewardsContext);
  const { userProfile } = UserStore.useUserStore;

  useEffect(() => {
    const { endDate } = reward;
    let newDateToUse: DateTime | undefined;

    if (!reward.redemptions.length) {
      if (reward.status !== types.AssetRewardStatus.Expired) {
        newDateToUse = endDate ? DateTime.fromJSDate(endDate) : undefined;
      }
    } else {
      const { expiresAt, status } = reward.redemptions[0];

      if (status !== types.AssetRedemptionStatus.Expired) {
        newDateToUse = expiresAt ? DateTime.fromJSDate(expiresAt) : undefined;
      }
    }

    setDateToUse(newDateToUse);
    if (newDateToUse) {
      setDiff(newDateToUse.diffNow(['days', 'hours', 'minutes', 'seconds']));
    }
  }, [reward]);

  const onLoop = useCallback(() => {
    if (dateToUse) setDiff(dateToUse.diffNow(['days', 'hours', 'minutes', 'seconds']));
  }, [dateToUse]);

  useInterval(onLoop, 1000);

  const title = useMemo(() => {
    if (!reward.redemptions.length) return t('timeToOptIn');

    const { status } = reward.redemptions[0];

    if (status === types.AssetRedemptionStatus.InProgress) {
      return t('completeIn');
    }

    return t(reward.hasPayout ? 'timeToRedeem' : 'timeToSubmit');
  }, [reward, t]);

  useEffect(() => {
    if (!diff) return;

    if (diff.seconds <= 0) {
      if (userProfile && userProfile.intercom?.uuid) {
        fetchRewards(userProfile.intercom.uuid, RewardType.Available);
        fetchRewards(userProfile.intercom.uuid, RewardType.History);
      }
    }
  }, [diff, userProfile, fetchRewards]);

  if (!dateToUse) return null;

  return (
    <>
      <Body loading={!diff} className='flex gap-4 bg-color-background-info-subtle p-8'>
        <span>{title}</span>

        {diff && (
          <>
            <span>{Math.floor(diff.days)}</span>
            <span>{t('days')}</span>
            {diff && <span>{Math.floor(diff.hours)}</span>}
            <span>{t('hours')}</span>
            {diff && <span>{Math.floor(diff.minutes)}</span>}
            <span>{t('minutes')}</span>
            {diff && <span>{Math.floor(diff.seconds)}</span>}
            <span>{t('seconds')}</span>
          </>
        )}
      </Body>
    </>
  );
});

export { RewardCardCountdown };
